/**
 * Responsibility: Implement HTML-first smart tooltip data synchronization.
 * Scope: Owns DOM synchronization of tooltip content; visibility/positioning handled by CSS.
 */

export function setupDecoratedTooltips() {
  if (typeof document === 'undefined') return () => {}
  const root = document.body
  if (!root) return () => {}

  const interactiveSelector = [
    'button',
    '[role="button"]',
    'a[href]',
    'summary',
    '[data-tip]',
    '.has-tooltip',
  ].join(',')

  const readTipText = (el: HTMLElement) => {
    // Priority 1: data-tip (explicit)
    const explicit = String(el.getAttribute('data-tip') || '').trim()
    if (explicit) return explicit

    // Priority 2: title
    const title = String(el.getAttribute('title') || '').trim()
    if (title) return title

    // Priority 3: aria-label
    const aria = String(el.getAttribute('aria-label') || '').trim()
    if (aria) {
      if (/^(open|close)\b/i.test(aria)) return ''
      return aria
    }

    // Priority 4: Text content if short
    const text = String(el.textContent || '').replace(/\s+/g, ' ').trim()
    if (/^(open|close)\b/i.test(text)) return ''
    if (text.length > 1 && text.length <= 70) return text
    return ''
  }

  const readTipHtml = (node: HTMLElement) => {
    const tooltip = node.querySelector(':scope > .tooltip') as HTMLElement | null
    return String(tooltip?.innerHTML || '').trim()
  }

  const setGeneratedTooltipBody = (tooltipEl: HTMLElement, tipText: string) => {
    tooltipEl.dataset.tipGenerated = 'true'
    tooltipEl.dataset.tipText = tipText
    tooltipEl.textContent = ''
    tooltipEl.setAttribute('aria-hidden', 'true')
  }

  const writeGeneratedTip = (node: HTMLElement, tipText: string) => {
    if (node.dataset.tipLock === 'off') return
    if (!tipText) {
      if (node.dataset.tipGenerated === 'true') {
        node.removeAttribute('data-tip')
        delete node.dataset.tipGenerated
      }
      return
    }
    if (node.getAttribute('data-tip') !== tipText) node.setAttribute('data-tip', tipText)
    node.dataset.tipGenerated = 'true'
  }

  const syncNodeTip = (node: HTMLElement) => {
    if (!node) return
    if (node.dataset.tipLock === 'off') {
      writeGeneratedTip(node, '')
      return
    }
    
    // If there is any manual or existing tooltip inside, skip auto-sync
    if (node.querySelector(':scope > .tooltip')) {
      writeGeneratedTip(node, readTipText(node) || readTipHtml(node))
      if (!node.classList.contains('tooltip-wrap')) node.classList.add('tooltip-wrap')
      return
    }

    const tipHTML = readTipText(node)
    const hasTip = tipHTML.length > 0

    let tooltipEl = node.querySelector(':scope > .tooltip') as HTMLElement
    
    if (!hasTip) {
      writeGeneratedTip(node, '')
      if (tooltipEl && !tooltipEl.hasAttribute('data-tip-custom')) {
        tooltipEl.remove()
        node.classList.remove('tooltip-wrap')
      }
      return
    }

    // Ensure we don't have title flickering
    if (node.hasAttribute('title')) {
      node.dataset.nativeTitle = node.getAttribute('title') || ''
      node.removeAttribute('title')
    }

    writeGeneratedTip(node, tipHTML)
    if (!node.classList.contains('tooltip-wrap')) node.classList.add('tooltip-wrap')

    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.className = 'tooltip'
      // Default to bottom for HUD elements, top otherwise
      const position = node.closest('scene-action-rail') ? 'bottom' : (node.getAttribute('data-tip-side') || 'top')
      tooltipEl.setAttribute('data-position', position)
      tooltipEl.setAttribute('aria-hidden', 'true')
      node.appendChild(tooltipEl)
    }

    setGeneratedTooltipBody(tooltipEl, tipHTML)
  }

  const syncTree = (scope: HTMLElement | Document) => {
    if (!scope) return
    if (scope instanceof HTMLElement && (scope.matches(interactiveSelector) || scope.hasAttribute('data-tip'))) syncNodeTip(scope)
    const nodes = scope.querySelectorAll(`${interactiveSelector}, [data-tip]`)
    nodes.forEach(node => syncNodeTip(node as HTMLElement))
  }

  syncTree(document)

  let pendingSync = false
  const syncQueue = new Set<HTMLElement>()

  const flushSync = () => {
    if (!pendingSync) return
    syncQueue.forEach(node => syncNodeTip(node))
    syncQueue.clear()
    pendingSync = false
  }

  const observer = new MutationObserver((mutations) => {
    let fullSyncNeeded = false
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        syncQueue.add(mutation.target as HTMLElement)
        pendingSync = true
      } else if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            if (node.matches(interactiveSelector) || node.hasAttribute('data-tip') || node.querySelector(`${interactiveSelector}, [data-tip]`)) {
              fullSyncNeeded = true
            }
          }
        })
      }
    }
    
    if (fullSyncNeeded) {
      syncTree(document)
      syncQueue.clear()
      pendingSync = false
    } else if (pendingSync) {
      requestAnimationFrame(flushSync)
    }
  })
  
  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['title', 'aria-label', 'data-tip'],
  })

  let touchTimer = 0
  let touchTarget: HTMLElement | null = null

  const clearForcedTip = (target: HTMLElement | null) => {
    if (!target) return
    target.classList.remove('am2-tip-force')
    target.removeAttribute('data-tip-visible')
  }

  const clearTouchTimer = () => {
    if (touchTimer) window.clearTimeout(touchTimer)
    touchTimer = 0
  }

  // Touch devices need an explicit long-press bridge because hover/focus is not enough for discovery.
  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch') return
    const target = (event.target as HTMLElement | null)?.closest('.tooltip-wrap') as HTMLElement | null
    if (!target || target.dataset.tipLock === 'off') return
    clearTouchTimer()
    clearForcedTip(touchTarget)
    touchTarget = target
    touchTimer = window.setTimeout(() => {
      if (!touchTarget) return
      touchTarget.classList.add('am2-tip-force')
      touchTarget.setAttribute('data-tip-visible', 'true')
      touchTimer = 0
    }, 420)
  }

  const onPointerEnd = () => {
    clearTouchTimer()
    const active = touchTarget
    if (!active) return
    window.setTimeout(() => {
      if (touchTarget === active) {
        clearForcedTip(active)
        touchTarget = null
      }
    }, 700)
  }

  root.addEventListener('pointerdown', onPointerDown, true)
  root.addEventListener('pointerup', onPointerEnd, true)
  root.addEventListener('pointercancel', onPointerEnd, true)
  root.addEventListener('pointerleave', onPointerEnd, true)

  return () => {
    observer.disconnect()
    clearTouchTimer()
    clearForcedTip(touchTarget)
    root.removeEventListener('pointerdown', onPointerDown, true)
    root.removeEventListener('pointerup', onPointerEnd, true)
    root.removeEventListener('pointercancel', onPointerEnd, true)
    root.removeEventListener('pointerleave', onPointerEnd, true)
    document.querySelectorAll('.tooltip').forEach(el => {
      if (!el.hasAttribute('data-tip-custom')) {
        el.parentElement?.classList.remove('tooltip-wrap')
        el.remove()
      }
    })
    document.querySelectorAll('[data-tip-generated="true"]').forEach((node) => {
      node.removeAttribute('data-tip')
      node.removeAttribute('data-tip-generated')
      node.removeAttribute('data-tip-visible')
      node.classList.remove('am2-tip-force')
    })
  }
}
